/**
 * session/league — thin, pure glue between the League data (data/quests/league),
 * the pure resume helpers (core/league) and the live GameSession. Keeps GameSession's
 * League verbs and the LeagueScene both slim: party building, the Hall of Fame snapshot,
 * and the town-celebration beat all live here as small pure functions.
 *
 * The renderer never owns rules: LeagueScene calls buildMemberParty for the battle
 * payload; GameSession calls teamSnapshot + championCrownedBeat inside its verbs.
 */
import type { Beat, HallOfFameRecord, MoveData, PokemonInstance, SpeciesData } from '@/shared';
import type { LeagueMember } from '@/data/quests/league.js';
import { makeInstance } from '@/society';
import { makeRng, hashString } from '@/core';

/** Just the dataset lookups a party build needs (structural — no bundle import churn). */
export interface PartyDataIndex {
  speciesById(id: string): SpeciesData | undefined;
  moveById(id: string): MoveData | undefined;
}

/**
 * Build a League member's faithful party from their authored {speciesId, level} list —
 * a verbatim mirror of GymScene.buildLeaderParty (makeInstance + full-heal + PP hydrate).
 * makeInstance mints move slots with pp/maxPp = 0 (society is pure, no move data), so we
 * hydrate real PP from the DataIndex here. Any species the dataset lacks is skipped
 * (never a crash on a thin build). Deterministic per member ⇒ stable teams on rematch.
 */
export function buildMemberParty(
  index: PartyDataIndex,
  seed: number,
  member: LeagueMember,
): PokemonInstance[] {
  const rng = makeRng((seed ^ hashString(`league|${member.eliteId}`)) >>> 0);
  const owner = { id: `league-${member.eliteId}`, name: member.name };
  const party: PokemonInstance[] = [];
  for (const spec of member.party) {
    const species = index.speciesById(spec.speciesId);
    if (!species) continue;
    const mon = makeInstance(rng, species, spec.level, owner);
    mon.currentHp = mon.maxHp;
    mon.status = 'none';
    mon.statusTurns = 0;
    mon.moves = mon.moves.map((slot) => {
      if (slot.maxPp > 0) return { ...slot, pp: slot.maxPp };
      const maxPp = index.moveById(slot.moveId)?.pp ?? 5;
      return { ...slot, pp: maxPp, maxPp };
    });
    party.push(mon);
  }
  return party;
}

/** Snapshot the current team into a warm Hall of Fame page (a scrapbook, not a score). */
export function teamSnapshot(
  team: readonly PokemonInstance[],
  inGameDate: string,
  at: number,
): HallOfFameRecord {
  return {
    clearedAt: at,
    inGameDate,
    team: team.map((m) => ({
      speciesId: m.speciesId,
      nickname: m.nickname ?? null,
      level: m.level,
    })),
  };
}

/**
 * The town-celebration beat fired when the Champion is beaten. subjectTrainerId 'player'
 * so the voice layer (champion-crowned template) phrases it about the player, warm by
 * construction. Global channel — the whole region cheers. `clears` lets a rematch read
 * a touch differently if the voice wants it, but the template never gloats or compares.
 */
export function championCrownedBeat(at: number, clears: number): Beat {
  return {
    id: `champion-crowned-${at}`,
    kind: 'champion-crowned',
    priority: 'highlight',
    subjectTrainerId: 'player',
    involvedTrainerIds: ['player'],
    channel: 'global',
    data: { byPlayer: true, warm: true, clears },
    createdAt: at,
  };
}
